import { z } from 'zod';
import { IsoDateTime, TenantId } from './common.ts';

/**
 * 17a (ADR-019): resource scope, actor entitlement and requested scope are three different things.
 *
 * - `ResourceScope` is an attribute of a resource (case, task, approval request): one canonical value per
 *   pack-declared dimension, e.g. `{ entity: 'mrd-hq', costCentre: 'CC-100' }`. Core never names a dimension.
 * - `Entitlement` is what an actor is allowed to do within a dimension. It comes from tenant/IdP configuration,
 *   never from a request payload.
 * - A requested scope (on a task or a spawn request) is a claim that `checkScope` verifies against entitlements.
 */

/** Pack-declared dimension name: lower camel case, e.g. `entity`, `costCentre`, `project`. */
export const ScopeDimension = z
  .string()
  .regex(/^[a-z][a-zA-Z0-9]{0,62}$/, 'scope dimension must be lower camel case')
  .meta({ id: 'ScopeDimension' });
export type ScopeDimension = z.infer<typeof ScopeDimension>;

/** Canonical value of a dimension (the id in the authoritative system), e.g. `mrd-hq`. `*` is reserved. */
export const ScopeValue = z
  .string()
  .min(1)
  .max(200)
  .refine((v) => v !== ENTITLEMENT_WILDCARD, 'the wildcard `*` is valid only in entitlements')
  .meta({ id: 'ScopeValue' });

/** `*` in `Entitlement.values` means every value of the dimension (bindable through configuration only). */
export const ENTITLEMENT_WILDCARD = '*';

/** Resource attributes: one value per dimension. An empty object means "tenant-wide, no dimension". */
export const ResourceScope = z.record(ScopeDimension, ScopeValue).meta({ id: 'ResourceScope' });
export type ResourceScope = z.infer<typeof ResourceScope>;

/** What an entitlement allows within its dimension values. `view` never implies `decide` or `act`. */
export const EntitlementGrant = z.enum(['view', 'decide', 'act']).meta({ id: 'EntitlementGrant' });
export type EntitlementGrant = z.infer<typeof EntitlementGrant>;

/** Where an entitlement came from. Packs never self-assign: `idp` claims or tenant `config` only. */
export const EntitlementSource = z.enum(['idp', 'config']).meta({ id: 'EntitlementSource' });
export type EntitlementSource = z.infer<typeof EntitlementSource>;

/**
 * One actor's authority, bound to the authenticated principal id (not a role).
 * - `kind: 'dimension'` (default): authority over the listed `values` of one `dimension`.
 * - `kind: 'tenant'`: explicit tenant-wide authority (every resource of the tenant, including dimension-less
 *   ones). Only tenant configuration can grant it (`source: 'config'`); never derived implicitly.
 */
export const Entitlement = z
  .object({
    tenantId: TenantId,
    principalId: z.string().min(1),
    kind: z.enum(['dimension', 'tenant']).default('dimension'),
    dimension: ScopeDimension.optional(),
    /** Canonical values, or `['*']` for every value of the dimension. */
    values: z.array(z.string().min(1)).min(1).optional(),
    grants: z.array(EntitlementGrant).min(1),
    source: EntitlementSource,
    /** Role (pack-declared or platform) this entitlement was derived from, for audit. */
    viaRole: z.string().optional(),
    validFrom: IsoDateTime.optional(),
    validUntil: IsoDateTime.optional(),
  })
  .superRefine((e, ctx) => {
    if (e.kind === 'dimension' && (e.dimension === undefined || e.values === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'a dimension entitlement needs dimension and values' });
    }
    if (e.kind === 'tenant' && (e.dimension !== undefined || e.values !== undefined)) {
      ctx.addIssue({ code: 'custom', message: 'a tenant entitlement has no dimension or values' });
    }
    if (e.kind === 'tenant' && e.source !== 'config') {
      ctx.addIssue({ code: 'custom', message: 'tenant-wide entitlements are granted by tenant config only' });
    }
  })
  .meta({ id: 'Entitlement' });
export type Entitlement = z.infer<typeof Entitlement>;

/** Added by 20a: `ScopeCheckRequest.principalFacts` of an authenticated principal (roles, `attributes.domains`). */
export function principalFactsOf(principal: {
  roles?: readonly string[];
  attributes?: Readonly<Record<string, unknown>>;
}): { roles: string[]; domains?: string[] } {
  const domains = principal.attributes?.domains;
  return {
    roles: [...(principal.roles ?? [])],
    ...(Array.isArray(domains)
      ? { domains: domains.filter((d): d is string => typeof d === 'string' && d.length > 0) }
      : {}),
  };
}

/** Platform permission names introduced by 17a. `observer` = view within entitled scope, never decide or act. */
export const PROCESS_PERMISSIONS = {
  caseView: 'case.view',
  caseAct: 'case.act',
  approvalDecideScoped: 'approval.decide.scoped',
  observer: 'observer',
} as const;
export const ProcessPermission = z
  .enum(['case.view', 'case.act', 'approval.decide.scoped', 'observer'])
  .meta({ id: 'ProcessPermission' });
export type ProcessPermission = z.infer<typeof ProcessPermission>;

/** The entitlement grant each process permission needs on the resource scope. */
export const PERMISSION_GRANT: Readonly<Record<ProcessPermission, EntitlementGrant>> = {
  'case.view': 'view',
  'case.act': 'act',
  'approval.decide.scoped': 'decide',
  observer: 'view',
};

/** A scope check: may `principalId` use `grant` on a resource with `resource` scope? */
export const ScopeCheckRequest = z
  .object({
    tenantId: TenantId,
    principalId: z.string().min(1),
    grant: EntitlementGrant,
    /** The resource's own scope (from the resource record, never from the caller). */
    resource: ResourceScope,
    /**
     * Dimensions the owning pack declared for this resource type; a missing one denies (fail closed). Required:
     * the caller states `[]` explicitly for a resource type without dimensions.
     */
    requiredDimensions: z.array(ScopeDimension),
    /** Evaluation time for `validFrom`/`validUntil`; defaults to now. */
    at: IsoDateTime.optional(),
    /**
     * Added by 20a: the authenticated principal's roles and task domains (from the session, never from a payload).
     * When given, the store adds the entitlements its tenant configuration derives from roles
     * (`cases.roleEntitlements`) and the domain `view` grants (`domain` dimension) before evaluating.
     */
    principalFacts: z
      .object({ roles: z.array(z.string().min(1)), domains: z.array(z.string().min(1)).optional() })
      .optional(),
  })
  .meta({ id: 'ScopeCheckRequest' });
export type ScopeCheckRequest = z.infer<typeof ScopeCheckRequest>;
export type ScopeCheckRequestInput = z.input<typeof ScopeCheckRequest>;

export const ScopeDenialReason = z
  .enum([
    'tenant_mismatch',
    'missing_dimension',
    'not_entitled',
    'grant_missing',
    'expired',
    'tenant_grant_required',
  ])
  .meta({ id: 'ScopeDenialReason' });
export type ScopeDenialReason = z.infer<typeof ScopeDenialReason>;

export const ScopeCheckResponse = z
  .object({
    allowed: z.boolean(),
    /** One entry per failing dimension (empty when allowed). */
    denials: z.array(z.object({ dimension: z.string(), reason: ScopeDenialReason })).default([]),
    /** Entitlements that satisfied each dimension (audit evidence). */
    matched: z.array(Entitlement).default([]),
  })
  .meta({ id: 'ScopeCheckResponse' });
export type ScopeCheckResponse = z.infer<typeof ScopeCheckResponse>;

function entitlementActive(e: Entitlement, at: string): boolean {
  const t = Date.parse(at);
  if (e.validFrom !== undefined && Date.parse(e.validFrom) > t) return false;
  if (e.validUntil !== undefined && Date.parse(e.validUntil) <= t) return false;
  return true;
}

/**
 * Pure reference evaluation of a scope check. Rules (ADR-019):
 * - every `requiredDimensions` entry must be present on the resource, else `missing_dimension` (fail closed);
 * - an active `tenant` entitlement of the same tenant with `grant` covers every dimension;
 * - otherwise every dimension of the resource must be covered by one active dimension entitlement of the same
 *   tenant and principal holding `grant` with the resource value (or `*`);
 * - a resource with **no** dimensions is matched only by a `tenant` entitlement, never by a dimensional grant
 *   (`tenant_grant_required`);
 * - entitlements of another tenant never count.
 */
export function checkScope(
  request: ScopeCheckRequestInput,
  entitlements: readonly Entitlement[],
): ScopeCheckResponse {
  const req = ScopeCheckRequest.parse(request);
  const at = req.at ?? new Date().toISOString();
  const mine = entitlements.filter((e) => e.principalId === req.principalId);
  const sameTenant = mine.filter((e) => e.tenantId === req.tenantId);
  const denials: ScopeCheckResponse['denials'] = [];
  const matched: Entitlement[] = [];

  for (const dim of req.requiredDimensions) {
    if (req.resource[dim] === undefined) denials.push({ dimension: dim, reason: 'missing_dimension' });
  }
  if (denials.length > 0) return { allowed: false, denials, matched };
  const tenantGrant = sameTenant.find(
    (e) => e.kind === 'tenant' && e.grants.includes(req.grant) && entitlementActive(e, at),
  );
  if (tenantGrant) return { allowed: true, denials: [], matched: [tenantGrant] };
  const dims = Object.keys(req.resource);
  if (dims.length === 0) {
    denials.push({
      dimension: '*',
      reason: mine.length > 0 && sameTenant.length === 0 ? 'tenant_mismatch' : 'tenant_grant_required',
    });
  }
  for (const dim of dims) {
    const value = req.resource[dim] as string;
    const forDim = sameTenant.filter((e) => e.kind === 'dimension' && e.dimension === dim);
    const covering = forDim.filter(
      (e) => e.values?.includes(value) === true || e.values?.includes(ENTITLEMENT_WILDCARD) === true,
    );
    const withGrant = covering.filter((e) => e.grants.includes(req.grant));
    const active = withGrant.find((e) => entitlementActive(e, at));
    if (active) {
      matched.push(active);
      continue;
    }
    let reason: ScopeDenialReason = 'not_entitled';
    if (withGrant.length > 0) reason = 'expired';
    else if (covering.length > 0) reason = 'grant_missing';
    else if (sameTenant.length === 0 && mine.length > 0) reason = 'tenant_mismatch';
    denials.push({ dimension: dim, reason });
  }
  return { allowed: denials.length === 0, denials, matched };
}

/**
 * True when `inner` is the same as or narrower than `outer`: every dimension fixed by `outer` is fixed to the same
 * value in `inner`. Structural only: `outer = {}` returns true and does **not** mean tenant-wide authority; use
 * `delegationCovers` / `Delegation.tenantWide` for authority (ADR-015, ADR-019).
 */
export function scopeWithin(inner: ResourceScope, outer: ResourceScope): boolean {
  return Object.entries(outer).every(([dim, value]) => inner[dim] === value);
}

/**
 * Pack-declared role in `module.yaml` `spec.roles`. Bound to people through IdP claims or tenant config only.
 * Names and permissions are namespaced by the pack (`packRoleIssues`, enforced by `ModuleManifest`).
 */
export const PackRole = z
  .object({
    /** `<packName>.<role>`, e.g. `p2p.treasury-signatory`. */
    name: z.string().regex(/^[a-z][a-z0-9_.-]*$/, 'role name must be lowercase'),
    description: z.string().optional(),
    /** `<packName>.<verb>` permissions, or entries of the `PROCESS_PERMISSIONS` allow-list. */
    permissions: z.array(z.string().min(1)).default([]),
    bindsVia: EntitlementSource,
  })
  .meta({ id: 'PackRole' });
export type PackRole = z.infer<typeof PackRole>;

const PROCESS_PERMISSION_VALUES: readonly string[] = Object.values(PROCESS_PERMISSIONS);

/**
 * Problems of a pack's role declarations (empty = valid). A pack may declare only roles named `<packName>.*` and
 * grant only `<packName>.*` permissions or the `PROCESS_PERMISSIONS` allow-list; platform role names
 * (`admin`, `approver`, …) and platform permissions (`tasks.*`, `studio.*`, `registry.*`, …) are refused, so
 * installing a pack can never mint platform privileges.
 */
export function packRoleIssues(packName: string, roles: readonly PackRole[]): string[] {
  const issues: string[] = [];
  const prefix = `${packName}.`;
  const names = roles.map((r) => r.name);
  for (const r of roles) {
    if (!r.name.startsWith(prefix) || r.name.length === prefix.length) {
      issues.push(`role "${r.name}" must be named ${prefix}<role>`);
    }
    for (const p of r.permissions) {
      if (!p.startsWith(prefix) && !PROCESS_PERMISSION_VALUES.includes(p)) {
        issues.push(
          `role "${r.name}" grants "${p}": only ${prefix}* or ${PROCESS_PERMISSION_VALUES.join(', ')}`,
        );
      }
    }
  }
  for (const n of new Set(names.filter((n, i) => names.indexOf(n) !== i)))
    issues.push(`duplicate role "${n}"`);
  return issues;
}
